import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

/**
 * Probes /api/me to check whether a valid session cookie exists.
 *
 * Returns true when the session is valid, null when it is not.
 * Uses a plain axios call (not the app's interceptor-equipped instances)
 * so that a 401 "not logged in" response does not trigger the global
 * appLogout handler and cause a redirect loop for unauthenticated guests.
 */
export async function checkSession(): Promise<boolean | null> {
  try {
    await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/me`, {
      withCredentials: true,
      timeout: 10000,
    });
    return true;
  } catch (error: unknown) {
    // 401 is expected when there is no valid session
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (401 !== status) {
      console.warn('Session probe failed with unexpected error', { status, error });
    }
    return null;
  }
}

export const sessionCheckQueryKey = () => ['customer', 'session-check'] as const;

export function useSessionCheckQuery(enabled = true) {
  return useQuery({
    queryKey: sessionCheckQueryKey(),
    queryFn: checkSession,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
